"use client";

import type React from "react";
import { useRef, useState, useEffect, useCallback } from "react";
import { Icon } from "@/components/ui/icon";

export function HorizontalScrollbar({
  scrollRef,
}: {
  scrollRef: React.RefObject<HTMLDivElement | null>;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState({ width: 0, left: 0 });
  const [hasScroll, setHasScroll] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const overflow = scrollWidth > clientWidth + 1;
      setHasScroll(overflow);
      if (!overflow) return;

      const track = trackRef.current;
      if (!track) return;
      const trackW = track.clientWidth;
      const tw = Math.max(30, trackW * (clientWidth / scrollWidth));
      const maxScroll = scrollWidth - clientWidth;
      const maxLeft = trackW - tw;
      setThumb({
        width: tw,
        left: maxScroll > 0 ? (scrollLeft / maxScroll) * maxLeft : 0,
      });
    };

    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [scrollRef]);

  const handleThumbDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const el = scrollRef.current;
      const track = trackRef.current;
      if (!el || !track) return;

      const startX = e.clientX;
      const startScroll = el.scrollLeft;
      const trackW = track.clientWidth;
      const tw = thumb.width;
      const maxScroll = el.scrollWidth - el.clientWidth;
      const maxLeft = trackW - tw;

      const onMove = (me: MouseEvent) => {
        const ratio = maxLeft > 0 ? (me.clientX - startX) / maxLeft : 0;
        el.scrollLeft = Math.max(
          0,
          Math.min(maxScroll, startScroll + ratio * maxScroll),
        );
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [scrollRef, thumb.width],
  );

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      const el = scrollRef.current;
      const track = trackRef.current;
      if (!el || !track) return;
      const rect = track.getBoundingClientRect();
      const ratio = (e.clientX - rect.left) / rect.width;
      el.scrollTo({
        left: ratio * (el.scrollWidth - el.clientWidth),
        behavior: "smooth",
      });
    },
    [scrollRef],
  );

  const scrollBy = useCallback(
    (amount: number) =>
      scrollRef.current?.scrollBy({ left: amount, behavior: "smooth" }),
    [scrollRef],
  );

  if (!hasScroll) return null;

  return (
    <div
      data-slot="ar-table-scrollbar"
      className="flex items-center gap-1 px-3 py-2 bg-surface-primary border-t border-border shrink-0"
    >
      <button
        type="button"
        onClick={() => scrollBy(-200)}
        className="flex items-center justify-center shrink-0 size-[10px] cursor-pointer text-contrast/40 hover:text-contrast/60 transition-colors"
      >
        <Icon name="IconChevronLeft" size={10} />
      </button>
      <div
        ref={trackRef}
        className="flex-1 h-1 rounded-full bg-contrast/5 relative cursor-pointer"
        onClick={handleTrackClick}
      >
        <div
          className="absolute top-0 h-1 rounded-full bg-contrast/60 cursor-grab active:cursor-grabbing hover:bg-contrast/70 transition-colors"
          style={{ width: thumb.width, left: thumb.left }}
          onMouseDown={handleThumbDown}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <button
        type="button"
        onClick={() => scrollBy(200)}
        className="flex items-center justify-center shrink-0 size-[10px] cursor-pointer text-contrast/40 hover:text-contrast/60 transition-colors"
      >
        <Icon name="IconChevronRight" size={10} />
      </button>
    </div>
  );
}
